/* eslint-disable react/prop-types */
import { useEffect } from "react";
import { MyFormData } from "./form-context";
import { Box, Tooltip, Typography, Button } from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

function MyStepper({ steps }) {
  const { currentStep, validationFunctions, setCurrentStep, complete } =
    MyFormData();
  const theme = useTheme();
  const { t } = useTranslation();

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme.palette.mode);
  }, [theme.palette.mode]);

  // Calculate progress percentage with adjustment for step count
  const progressPercentage = Math.min(
    ((currentStep - 1) / (steps.length - 1)) * 100,
    100
  );

  // Check if a step is accessible
  const isStepAccessible = (stepIndex) => {
    const targetStep = stepIndex + 1;
    if (targetStep === 1) return true;
    if (targetStep < currentStep) return true;
    for (let i = 0; i < targetStep - 1; i++) {
      if (
        validationFunctions &&
        validationFunctions[i] &&
        !validationFunctions[i]()
      ) {
        return false;
      }
    }
    return true;
  };

  // Handle step click
  const handleStepClick = (index) => {
    const targetStep = index + 1;
    if (isStepAccessible(index) && targetStep !== currentStep) {
      setCurrentStep(targetStep);
    }
  };

  const CIRCLE_SIZE = 28;
  const PROGRESS_HEIGHT = 6;
  const CIRCLE_MARGIN = CIRCLE_SIZE / 4;
  const CIRCLE_BORDER = 3;

  // Check if a step is accessible for navigation
  const canNavigate = (direction) => {
    if (direction === "prev") {
      return currentStep > 1 && !complete;
    }
    if (direction === "next") {
      return (
        currentStep < steps.length &&
        validationFunctions[currentStep - 1]() &&
        !complete
      );
    }
    return false;
  };

  // Handle navigation
  const handleNavigation = (direction) => {
    if (direction === "prev" && canNavigate("prev")) {
      setCurrentStep(currentStep - 1);
    } else if (direction === "next" && canNavigate("next")) {
      setCurrentStep(currentStep + 1);
    }
  };

  // Navigation button styles
  const getNavButtonStyles = (direction) => ({
    position: "absolute",
    top: CIRCLE_SIZE / 2 - PROGRESS_HEIGHT / 2 + 15,
    transform: "translateY(-50%)",
    [direction === "prev" ? "left" : "right"]: -32,
    minWidth: 100,
    height: 40,
    px: 3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    background: (theme) =>
      theme.palette.mode === "light"
        ? canNavigate(direction)
          ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
          : alpha(theme.palette.action.disabled, 0.08)
        : canNavigate(direction)
          ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)}, ${alpha(theme.palette.primary.dark, 0.9)})`
          : alpha(theme.palette.action.disabled, 0.12),
    color: canNavigate(direction) ? "common.white" : "text.disabled",
    borderRadius: 2,
    border: "1px solid",
    borderColor: canNavigate(direction) ? "transparent" : "divider",
    boxShadow: canNavigate(direction)
      ? (theme) =>
          `0 8px 16px ${alpha(theme.palette.primary.main, theme.palette.mode === "light" ? 0.25 : 0.4)}`
      : "none",
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.9375rem",
    cursor: canNavigate(direction)
      ? "pointer !important"
      : "not-allowed !important",

    "&:hover": canNavigate(direction)
      ? {
          background: (theme) =>
            theme.palette.mode === "light"
              ? `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`
              : `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.9)}, ${alpha(theme.palette.primary.main, 0.9)})`,
          boxShadow: (theme) =>
            `0 12px 20px ${alpha(theme.palette.primary.main, theme.palette.mode === "light" ? 0.35 : 0.5)}`,
          transform: "translateY(-50%) translateX(0)",
        }
      : {},
    "&:active": canNavigate(direction)
      ? {
          transform: "translateY(-48%)",
          boxShadow: (theme) =>
            `0 4px 8px ${alpha(theme.palette.primary.main, theme.palette.mode === "light" ? 0.2 : 0.3)}`,
        }
      : {},
    "&.Mui-disabled": {
      background: (theme) =>
        alpha(
          theme.palette.action.disabled,
          theme.palette.mode === "light" ? 0.08 : 0.12
        ),
      borderColor: "divider",
      color: "text.disabled",
      boxShadow: "none",
      cursor: "not-allowed !important",
    },
    "&:focus-visible": {
      outline: "none",
      boxShadow: (theme) =>
        `0 0 0 3px ${alpha(theme.palette.primary.main, 0.25)}`,
    },
    transition: theme.transitions.create(
      ["transform", "box-shadow", "background", "border-color"],
      {
        duration: 200,
      }
    ),
    zIndex: 2,
  });

  return (
    <Box
      sx={{
        width: "100%",
        position: "relative",
        pt: 1.5,
        pb: 0.5,
        px: 8,
      }}
    >
      {/* Navigation Buttons */}
      <Button
        variant="contained"
        size="small"
        onClick={() => handleNavigation("prev")}
        disabled={!canNavigate("prev")}
        sx={getNavButtonStyles("prev")}
        aria-label={t("myButton.prevAria")}
      >
        {t("myButton.prev") || "Prev"}
      </Button>

      <Button
        variant="contained"
        size="small"
        onClick={() => handleNavigation("next")}
        disabled={!canNavigate("next")}
        sx={getNavButtonStyles("next")}
        aria-label={t("myButton.nextAria")}
      >
        {t("myButton.next") || "Next"}
      </Button>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          position: "relative",
          mx: `${CIRCLE_MARGIN}px`,
          mb: 0.5,
        }}
      >
        {/* Progress Bar Background */}
        <Box
          sx={{
            position: "absolute",
            top: CIRCLE_SIZE / 2 - PROGRESS_HEIGHT / 2,
            left: CIRCLE_SIZE / 2,
            right: CIRCLE_SIZE / 2,
            height: PROGRESS_HEIGHT,
            bgcolor: alpha(theme.palette.grey[300], 0.3),
            borderRadius: PROGRESS_HEIGHT / 2,
          }}
        />

        {/* Progress Bar Fill */}
        <Box
          sx={{
            position: "absolute",
            top: CIRCLE_SIZE / 2 - PROGRESS_HEIGHT / 2,
            left: CIRCLE_SIZE / 2,
            height: PROGRESS_HEIGHT,
            width: `calc((100% - ${CIRCLE_SIZE}px) * ${progressPercentage / 100})`,
            borderRadius: PROGRESS_HEIGHT / 2,
            transition: "width 0.4s ease",
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`,
          }}
        />

        {/* Step Markers */}
        {steps.map((step, index) => {
          const isCompleted = index + 1 < currentStep;
          const isActive = index + 1 === currentStep;
          const isAccessible = isStepAccessible(index);

          return (
            <Box
              key={index}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                position: "relative",
                zIndex: 1,
              }}
            >
              <Tooltip
                title={isAccessible ? step : "Complete previous steps first"}
                placement="top"
                arrow
              >
                <Box
                  onClick={() => handleStepClick(index)}
                  sx={{
                    width: CIRCLE_SIZE,
                    height: CIRCLE_SIZE,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    bgcolor: isCompleted
                      ? theme.palette.success.main
                      : isActive
                        ? theme.palette.primary.main
                        : theme.palette.grey[300],
                    color:
                      isCompleted || isActive
                        ? "white"
                        : theme.palette.text.secondary,
                    border: `${CIRCLE_BORDER}px solid ${theme.palette.background.paper}`,
                    cursor: isAccessible ? "pointer" : "not-allowed",
                    opacity: isAccessible ? 1 : 0.5,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    transform: isActive ? "scale(1.1)" : "scale(1)",
                    boxShadow: isActive
                      ? `0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}, 0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`
                      : isCompleted
                        ? `0 2px 8px ${alpha(theme.palette.success.main, 0.3)}`
                        : "none",
                    "&:hover": isAccessible
                      ? {
                          transform: "scale(1.2)",
                          boxShadow: `0 0 0 4px ${alpha(
                            isCompleted
                              ? theme.palette.success.main
                              : theme.palette.primary.main,
                            0.2
                          )}, 0 4px 16px ${alpha(
                            isCompleted
                              ? theme.palette.success.main
                              : theme.palette.primary.main,
                            0.4
                          )}`,
                        }
                      : {},
                  }}
                >
                  {isCompleted ? (
                    <CheckCircle sx={{ fontSize: 18 }} />
                  ) : (
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: "currentColor",
                      }}
                    />
                  )}
                </Box>
              </Tooltip>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: isActive ? 600 : 400,
                  color: isActive
                    ? theme.palette.primary.main
                    : isCompleted
                      ? theme.palette.success.main
                      : theme.palette.text.secondary,
                  opacity: isAccessible ? 1 : 0.5,
                  maxWidth: 80,
                  textAlign: "center",
                  lineHeight: 1.2,
                  transition: "all 0.3s ease",
                }}
              >
                {step}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default MyStepper;
